import { FirebaseError, initializeApp } from "firebase/app";
import type { NotificationPermissionStatus } from "./notificationPermissionStatus";
import {
  Auth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  initializeAuth,
  getAuth,
  linkWithCredential,
  OAuthCredential,
  OAuthProvider,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
  UserCredential,
} from "firebase/auth";
import "firebase/messaging";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { t } from "i18next";
import { push, replace } from "react-router-redirect";
import {
  displayToast,
  displayActionableToast,
} from "../components/app/AppToast";
import { FIREBASE_CONFIG, FIREBASE_VAPID_KEY } from "../config";
import { adoptCacheOwner, localStoragePersister } from "./queryPersistence";
import { isStaging } from "../config/featureFlags";
import i18n, { DEFAULT_LANGUAGE } from "../config/i18n";
import {
  clearPostLoginRedirectPath,
  getPostLoginRedirectPath,
  memorizePostLoginRedirectTo,
} from "../domains/app/utils/postLoginRedirect";
import { resolvePostAuthDestination } from "../domains/app/utils/postAuthDestination";
import {
  hasPendingRoomInvite,
  roomInviteCodeStore,
} from "../domains/auth/stores/roomInviteCodeStore";
import { resetOnboardingFlow } from "../domains/auth/stores/onboardingFlowStore";
import { otpCredentialStore } from "../domains/auth/stores/otpCredentialStore";
import { desktopCodeRequestStore } from "../domains/auth/stores/desktopCodeRequestStore";
import { clearPendingEmailCode } from "../domains/auth/hooks/useEmailOtp";
import { clearVisualAbstractRunsOnDevice } from "../features/visual-abstract/visualAbstractWatchStore";
import { signupInFlightStore } from "../domains/auth/stores/signupInFlightStore";
import { signupSessionUidStore } from "../domains/auth/stores/signupSessionStore";
import { isAwaitingVisualAbstract } from "../features/visual-abstract/visualAbstractWatchStore";
import {
  getPushTokenStore,
  nativeFcmFreshnessStore,
} from "../domains/user/stores/pushTokensStore";
import * as Sentry from "@sentry/react";
import { sessionStore } from "../lib/store";
import { z } from "zod";
import { type Api, apiClient, clearApiCache, revalidateApiCache } from "./api";
import { ClientError } from "../../../api/src/shared/request";
import {
  ensureCurrentUser,
  replaceCurrentUserData,
  reloadCurrentUser,
} from "../domains/user/hooks/useCurrentUser";
import { getUserStartingPage } from "../domains/user/utils/getUserStartingPage";
import { isUserOnboarded } from "../domains/user/utils/isUserOnboarded";
import {
  gaEventAuthLogin,
  gaEventAuthSignup,
} from "../tools/analytics/domains/auth.analytics";
import {
  AuthIntent,
  gaEventAuthFailed,
  resolveAuthErrorCode,
  SignupSurface,
} from "../tools/analytics/domains/onboarding.analytics";
import { AuthResult } from "../domains/login/types/login.types";
import {
  androidGetFirebaseToken,
  androidGetFirebaseTokenAsync,
  androidGetNotificationState,
  androidRequestPushAuthorization,
  androidSignIn,
  isAndroidApp,
  setAndroidForegroundPushHandler,
  setAndroidTokenRefreshHandler,
} from "../tools/android";
import {
  iosAppleSignIn,
  iosDeleteFirebaseInstallation,
  iosGetFirebaseToken,
  iosGetNotificationState,
  iosGoogleSignIn,
  iosRequestPushAuthorization,
  isIOSApp,
  setIOSAuthRecoveryHandler,
  setIOSForegroundPushHandler,
  setIOSTokenRefreshHandler,
} from "../tools/ios";
import type { IOSAuthRecoveryData } from "../tools/ios";
import { getCurrentOS } from "../tools/utils";
import { SupportedLanguage } from "../interfaces";
import {
  normalizeLanguage,
  parseSupportedLanguage,
} from "../domains/app/utils/langUtils";
import { signupReferral } from "../tools/analytics/shareAttribution";
import {
  clearFirstTouch,
  signupFirstTouch,
} from "../tools/analytics/firstTouch";
import { currentClient } from "../common/hooks/useIsDesktopView";

const isWeb = !isIOSApp() && !isAndroidApp();

export type NotificationPermissionState = {
  status: NotificationPermissionStatus;
  canPromptInApp: boolean;
  mustOpenSettings: boolean;
};

export const NOTIFICATION_PERMISSION_CHANGE_EVENT =
  "notification-permission-change";

export type PushSyncReason =
  | "auth"
  | "foreground"
  | "permissionGranted"
  | "settingsReturn"
  | "tokenRefresh";

export type PushSyncOptions = {
  force?: boolean;
  reason: PushSyncReason;
};

type PushDevice = "ios" | "android" | "web";

/** Session-level throttle: resets on every page load, guarantees at least 1 sync per session. */
let lastSyncAt = 0;
const SYNC_THROTTLE_MS = 5 * 60 * 1000; // 5 minutes
const USER_READY_RETRY_DELAYS_MS = [250, 500] as const;
const PUSH_REGISTRATION_RETRY_DELAYS_MS = [250, 500] as const;
const SILENT_FIREBASE_ERRORS = [
  "auth/cancelled-popup-request",
  "auth/popup-closed-by-user",
  "iOS callback timeout",
  "ApiException: 12501", // Android Google Sign-In: user cancelled the account picker
];

// Window.services is declared in global.d.ts

const defaultApp = initializeApp(FIREBASE_CONFIG);

// In Node/Jest environment there is no IndexedDB / browser persistence available.
// Use `initializeAuth` with browser persistence only when running in a real browser.
// Auth is always defined in browser - Jest tests mock firebase separately.
export const auth: Auth =
  typeof indexedDB === "undefined"
    ? (undefined as unknown as Auth)
    : initializeAuth(defaultApp, {
        persistence: [
          indexedDBLocalPersistence,
          browserLocalPersistence,
          browserSessionPersistence,
        ],
      }); // Tests provide their own mock

getAuth(defaultApp).languageCode = i18n.resolvedLanguage ?? null;

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

export async function getFirebaseToken(): Promise<string | null> {
  await auth.authStateReady();
  return auth.currentUser?.getIdToken() ?? null;
}

/** Firebase Cloud Messaging (Push notification) */
let messaging: ReturnType<typeof getMessaging> | null = null;
const messagingSupportPromise = isSupported().then((supported) => {
  if (supported) {
    messaging = getMessaging(defaultApp);
  }
  return supported;
});

const swRegistrationPromise: Promise<ServiceWorkerRegistration | null> =
  isWeb && "serviceWorker" in navigator
    ? navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .catch(() => null)
    : Promise.resolve(null);
const DEFAULT_PUSH_SYNC_OPTIONS: PushSyncOptions = { reason: "foreground" };

function buildNotificationPermissionState(
  status: NotificationPermissionStatus
): NotificationPermissionState {
  return {
    status,
    canPromptInApp: status === "notDetermined",
    mustOpenSettings: status === "denied" && !isWeb,
  };
}

/**
 * The OS permission status to PERSIST — on the token registration, and on the
 * account through `pushOptOut` — or `null` when we genuinely could not read one.
 *
 * Deliberately not `getNotificationPermissionState()`: that helper coerces a
 * failed native bridge call to `"denied"`, which is the right default for
 * deciding whether to show an in-app prompt but disastrous to store — the api
 * skips `denied` devices when sending, so one flaky bridge call would silently
 * stop a working device's pushes until the next successful sync. An unreadable
 * state is `null` ("unknown"), which the api treats as sendable.
 *
 * Every caller that WRITES a permission belongs here. `syncPushOptOut` used the
 * coercing helper and stored its answer on the account, where the blast radius
 * is wider than one token: a single failed bridge call marked the account as
 * having refused push on every device at once, and nothing cleared it.
 */
export async function readOsNotificationPermission(): Promise<NotificationPermissionStatus | null> {
  if (isIOSApp()) {
    const state = await iosGetNotificationState();
    return state === null ? null : state.status;
  }
  if (isAndroidApp()) {
    const state = androidGetNotificationState();
    return state === null ? null : state.status;
  }
  if (typeof window === "undefined" || !("Notification" in window)) return null;
  // The browser's answer is authoritative, including its denial.
  if (Notification.permission === "granted") return "authorized";
  if (Notification.permission === "denied") return "denied";
  return "notDetermined";
}

export async function getNotificationPermissionState(): Promise<NotificationPermissionState> {
  if (isIOSApp()) {
    const state = await iosGetNotificationState();
    return buildNotificationPermissionState(state?.status ?? "denied");
  }

  if (isAndroidApp()) {
    const state = androidGetNotificationState();
    return buildNotificationPermissionState(state?.status ?? "denied");
  }

  if (typeof window === "undefined" || !("Notification" in window)) {
    return buildNotificationPermissionState("denied");
  }

  if (Notification.permission === "granted") {
    return buildNotificationPermissionState("authorized");
  }

  if (Notification.permission === "default") {
    return buildNotificationPermissionState("notDetermined");
  }

  return buildNotificationPermissionState("denied");
}

function dispatchNotificationPermissionChange(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(NOTIFICATION_PERMISSION_CHANGE_EVENT));
}

function shouldAttemptPushSync(
  token: string,
  device: PushDevice,
  options: PushSyncOptions
): boolean {
  if (options.force) return true;

  const lastSyncedToken = getPushTokenStore(device).get();
  if (lastSyncedToken !== token) return true;

  return Date.now() - lastSyncAt >= SYNC_THROTTLE_MS;
}

// One server read-back per app session to self-heal a token the backend lost
// (invalid-token purge after a reinstall, reseed, migration) that our LOCAL
// mirror still believes is synced. shouldAttemptPushSync compares against the
// local mirror, never the server — so without this a purged token only re-posts
// on the next cold start or once the 5-min throttle lapses, and a warm session
// keeps looking registered while the server holds nothing. Runs at most once per
// session and only on the throttle-skip path, so normal foregrounds pay nothing.
let serverReconcileDoneThisSession = false;

// `true` = the backend is missing this token, `false` = present, `null` = the
// read failed (indeterminate). Match by token id ALONE: the id uniquely
// identifies the registration, and a legacy row's `device` tag may be null — so
// keying on device too would read a still-registered token as missing.
async function serverIsMissingToken(token: string): Promise<boolean | null> {
  try {
    const { body } = await apiClient.get(
      "/webapp/private/notification/registrations"
    );
    return !body.tokens.some((t) => t.id === token);
  } catch {
    // A failed read is not evidence the server lost the token. Return null so
    // the caller does NOT consume the one-shot reconcile — the next foreground
    // retries. The request error is already reported by onRequestError.
    return null;
  }
}

async function registerPushToken(
  token: string,
  device: PushDevice,
  options: PushSyncOptions
): Promise<string> {
  if (!shouldAttemptPushSync(token, device, options)) {
    // The mirror says "already synced", but the mirror is local truth, not the
    // server's. Confirm against the server once per session; only fall through
    // to re-post if the backend is actually missing this token.
    if (serverReconcileDoneThisSession) return token;
    const missing = await serverIsMissingToken(token);
    // Only a DEFINITIVE answer consumes the one-shot; a failed read (null)
    // leaves it unset so a later foreground retries the reconcile.
    if (missing === null) return token;
    serverReconcileDoneThisSession = true;
    if (!missing) return token;
  }

  const synced = await trySyncRegistrationToken(token, device);
  if (synced) {
    lastSyncAt = Date.now();
    getPushTokenStore(device).set(token);
  }

  return token;
}

/** Web: gets FCM token and registers it with the backend. */
export async function registerWebPushToken(
  options: PushSyncOptions
): Promise<string | null> {
  const supported = await messagingSupportPromise;
  if (!supported || messaging === null) return null;
  try {
    const swRegistration = await swRegistrationPromise;
    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      ...(swRegistration !== null && {
        serviceWorkerRegistration: swRegistration,
      }),
    });
    if (!token) return null;
    return registerPushToken(token, "web", options);
  } catch (error) {
    console.error("Failed to register web push token:", error);
    return null;
  }
}

/**
 * Requests notification permission if not already granted.
 * Only call from explicit UX points (buttons, toggles, onboarding).
 * @returns true if permission granted, false otherwise
 */
export async function requestNotificationPermissionIfNeeded(): Promise<boolean> {
  if (isIOSApp()) {
    const granted = await iosRequestPushAuthorization();
    dispatchNotificationPermissionChange();
    return granted;
  }

  if (isAndroidApp()) {
    const granted = await androidRequestPushAuthorization();
    dispatchNotificationPermissionChange();
    return granted;
  }

  if (!("Notification" in window)) {
    dispatchNotificationPermissionChange();
    return false;
  }

  if (Notification.permission === "granted") {
    dispatchNotificationPermissionChange();
    return true;
  }

  if (Notification.permission === "denied") {
    dispatchNotificationPermissionChange();
    return false;
  }

  const permission = await Notification.requestPermission();
  dispatchNotificationPermissionChange();
  return permission === "granted";
}

/**
 * Syncs push token with backend if permission already granted (never prompts).
 * Safe to call from login flows and background contexts.
 * Deduplicates concurrent calls — only one sync runs at a time.
 */
let syncInFlight: Promise<void> | null = null;
export function syncPushTokenIfPossible(
  options: PushSyncOptions = DEFAULT_PUSH_SYNC_OPTIONS
): Promise<void> {
  if (syncInFlight !== null) return syncInFlight;
  syncInFlight = syncPushTokenImpl(options).finally(() => {
    syncInFlight = null;
  });
  return syncInFlight;
}

async function getAndroidPushToken(): Promise<string | null> {
  try {
    return (await androidGetFirebaseTokenAsync()) ?? androidGetFirebaseToken();
  } catch (error) {
    console.error("Failed to get Android push token asynchronously:", error);
    return androidGetFirebaseToken();
  }
}

// Session-level kill switch. Reset on every page load (cold start). Prevents
// us from re-paying the 5s iOS bridge timeout on every visibilitychange when
// the native handler is too old, or a real ghost is detected and the
// persistent sentinel is intentionally not set. Crucially we do NOT persist
// this to localStorage — that would lock the user out after a native upgrade-
// without-reinstall (localStorage survives, so a persisted "give up" flag
// would block the retry on the new native forever).
let nativeFcmRepairAbortedThisSession = false;

// Discriminated result of the native FID-delete bridge call. Three failure
// modes need distinct telemetry because they imply distinct followups:
//   - bridge_missing       → user on old native, no action possible until upgrade
//   - bridge_invocation_failed → bridge wired but flaky (timeout, throw, bad payload)
//   - native_delete_failed → Firebase Installations.delete() task itself failed natively
export type FidDeleteResult =
  | { status: "deleted" }
  | { status: "bridge_missing" }
  | { status: "bridge_invocation_failed" }
  | { status: "native_delete_failed" };

/**
 * One-shot native FCM repair: deletes the Firebase Installation ID so the SDK
 * mints a fresh identity (new FID, new FCM token, fresh APNs binding) on the
 * next `getToken`. The iOS Firebase SDK persists the FID in the Keychain which
 * survives uninstall — without an explicit delete, a reinstall can carry over
 * a stale FID whose server-side APNs binding is corrupted, silently breaking
 * push delivery. Runs at most once per install (gated by
 * `nativeFcmFreshnessStore` in localStorage, which is wiped on uninstall).
 *
 * Triggers on EVERY existing native user at rollout because the localStorage
 * key is brand-new — by design, this is a fleet-wide one-time repair, not
 * strictly a reinstall detector.
 *
 * The persistent sentinel is set only on a definitive success path. Any
 * failure mode (bridge unsupported, token unchanged even after FID delete)
 * leaves the sentinel untouched so the next cold start retries.
 *
 * iOS ONLY. Android wipes the FID with the app data on uninstall, so the defect
 * this repairs does not exist there, and the repair itself breaks push: the
 * Android FCM SDK keeps serving its cached token after `FirebaseInstallations
 * .delete()`, but the delete has already unregistered that token at FCM. The
 * client then re-registers a dead token, the sentinel is never set, and the
 * delete runs again on every cold start (Sentry JUISCI-APP-B, 100% Android;
 * 17–30% of Android sends `invalid` per week against 1–2% on iOS).
 */
export async function ensureFreshIosFcm(): Promise<string | null> {
  if (nativeFcmFreshnessStore.get()) return iosGetFirebaseToken();
  if (nativeFcmRepairAbortedThisSession) return iosGetFirebaseToken();

  const tokenBefore = await iosGetFirebaseToken();

  const deleteResult = await iosDeleteFirebaseInstallation();

  if (deleteResult.status !== "deleted") {
    // Bail without setting the sentinel — next cold start retries.
    nativeFcmRepairAbortedThisSession = true;
    const reason = deleteResult.status;
    let message: string;
    switch (reason) {
      case "bridge_missing":
        message =
          "push: native deleteFirebaseInstallation bridge missing (old client)";
        break;
      case "bridge_invocation_failed":
        message =
          "push: native deleteFirebaseInstallation bridge invocation failed";
        break;
      case "native_delete_failed":
        message = "push: native deleteFirebaseInstallation native task failed";
        break;
      default:
        throw new Error(reason satisfies never);
    }
    Sentry.captureMessage(message, {
      level: "info",
      tags: { device: "ios", reason },
    });
    return tokenBefore;
  }

  const tokenAfter = await iosGetFirebaseToken();

  // Firebase not ready (token still null both before and after). Don't burn
  // the sentinel — let the next sync retry.
  if (tokenBefore === null && tokenAfter === null) return null;

  // Either Firebase regurgitated the same token despite the FID delete, or
  // the post-delete getToken returned null. Either way: terminal failure on
  // this attempt. Do NOT set the sentinel — next cold start retries.
  if (tokenAfter === null || tokenAfter === tokenBefore) {
    nativeFcmRepairAbortedThisSession = true;
    Sentry.captureMessage("push: ghost token survived FID delete", {
      level: "warning",
      tags: { device: "ios" },
    });
    return tokenAfter === null ? tokenBefore : tokenAfter;
  }

  // Token changed (genuine refresh) or first-install (tokenBefore === null,
  // tokenAfter !== null — the FID delete on a fresh install is a no-op but
  // the subsequent getToken mints the install's first token). Either way the
  // repair is complete, so burn the sentinel and stop retrying.
  nativeFcmFreshnessStore.set(true);
  return tokenAfter;
}

/**
 * Tell the api that this device can no longer display a push.
 *
 * Uses the LAST SYNCED token rather than minting one: after a revocation the
 * platform may refuse to hand back a token at all, and the row we need to update
 * is keyed by the token we previously registered anyway. No stored token means we
 * never registered this device, so there is nothing to correct.
 *
 * `notDetermined` is reported too, not just `denied` — a user who reset
 * permissions should stop looking authorized. Only `denied` actually suppresses
 * sending (`isSendable`), so this is safe.
 */
async function reportPermissionRevocation(
  status: NotificationPermissionStatus
): Promise<void> {
  const device = isIOSApp() ? "ios" : isAndroidApp() ? "android" : "web";
  const lastSyncedToken = getPushTokenStore(device).get();
  if (lastSyncedToken === null || lastSyncedToken === "") return;

  // Bypasses `shouldAttemptPushSync`: the token has NOT changed (that throttle
  // exists to avoid re-posting an unchanged token), but the permission has, which
  // is precisely what we are here to record.
  const posted = await trySyncRegistrationToken(lastSyncedToken, device);
  if (!posted)
    console.error(`Failed to report notification permission "${status}"`);
}

async function syncPushTokenImpl(options: PushSyncOptions): Promise<void> {
  const permissionState = await getNotificationPermissionState();
  if (permissionState.status !== "authorized") {
    // A REVOCATION is the one case the api most needs to hear about, and it
    // arrives here — not below. Everything past this guard is token-minting work
    // that only makes sense while authorized, so reporting the denial has to
    // happen before we return, against the token we last synced.
    //
    // Without this the whole permission signal is inert for its actual purpose:
    // the api would only ever be told "authorized", and a user who switched
    // notifications off in system settings would keep counting as reachable.
    await reportPermissionRevocation(permissionState.status);
    return;
  }

  if (isIOSApp()) {
    try {
      const token = await ensureFreshIosFcm();
      if (!token) {
        // Authorized, but Firebase handed back no token — usually the APNs↔FCM
        // binding hasn't landed yet right after a reinstall/cold start and the
        // sync raced it. Nothing to post now; recovery is the native
        // token-refresh callback firing once the binding lands (registered
        // unconditionally below). Capture it to measure how often this races.
        Sentry.captureMessage("push: iOS FCM token unavailable at sync", {
          level: "info",
          tags: { reason: options.reason },
        });
        return;
      }
      await registerPushToken(token, "ios", options);
    } catch (error) {
      console.error("Failed to sync iOS push token:", error);
    }
    return;
  }

  if (isAndroidApp()) {
    try {
      // No FID repair on Android: see `ensureFreshIosFcm`.
      const token = await getAndroidPushToken();
      if (!token) {
        // See the iOS branch: authorized but no token yet (FCM not ready right
        // after a reinstall/cold start). Recovery is the token-refresh callback;
        // capture it to measure the race.
        Sentry.captureMessage("push: Android FCM token unavailable at sync", {
          level: "info",
          tags: { reason: options.reason },
        });
        return;
      }
      await registerPushToken(token, "android", options);
    } catch (error) {
      console.error("Failed to sync Android push token:", error);
    }
    return;
  }

  await registerWebPushToken(options);
  void setupWebForegroundNotificationHandler();
}

let webUnsubscribeOnMessage: (() => void) | null = null;

// Payload forwarded by the native layers (iOS willPresent / Android
// onMessageReceived) when an FCM push arrives while the app is in foreground.
const foregroundPushSchema = z.object({
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
});

/** Displays an in-app toast for a push received while the app is open. Tapping it navigates to the notification's deep-link. */
function handleForegroundPush(payload: unknown): void {
  const parsed = foregroundPushSchema.safeParse(payload);
  if (!parsed.success) {
    console.error("Invalid foreground push payload:", parsed.error);
    return;
  }
  const { title, body, link } = parsed.data;
  if (title === "" && body === "") return;
  // A poster this device is waiting on is announced by `useVisualAbstractWatcher`
  // within one poll of this push, on a card that holds the artifact: it wears the
  // poster's own mark and its View opens the preview, where this one can only
  // hand the notification router an id to resolve. Two toasts for one event is
  // worse than losing the weaker of the two.
  if (isAwaitingVisualAbstract()) return;
  // Only allow relative paths (block protocol-relative //evil.com)
  const safePath =
    link !== null && link.startsWith("/") && !link.startsWith("//")
      ? link
      : null;
  // The push writes its own two lines: FCM's `title` names the event and `body`
  // names the object. Joining them into one sentence is what produced a toast
  // taller than the article behind it.
  const content =
    title === ""
      ? { title: body, description: null }
      : { title, description: body === "" ? null : body };
  displayActionableToast(content, "info", () => {
    if (safePath !== null) push(safePath);
  });
}

/** Web only: displays an in-app toast when an FCM message arrives while the app is in foreground. */
export async function setupWebForegroundNotificationHandler() {
  const supported = await messagingSupportPromise;
  if (!supported || messaging === null || webUnsubscribeOnMessage !== null)
    return;
  webUnsubscribeOnMessage = onMessage(messaging, (payload) => {
    const link = payload.data === undefined ? undefined : payload.data.link;
    handleForegroundPush({
      title:
        payload.notification === undefined ||
        payload.notification.title === undefined
          ? ""
          : payload.notification.title,
      body:
        payload.notification === undefined ||
        payload.notification.body === undefined
          ? ""
          : payload.notification.body,
      link: typeof link === "string" ? link : null,
    });
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function waitForBackendUserReady(): Promise<boolean> {
  if ((await ensureCurrentUser()) !== null) return true;
  for (const delayMs of USER_READY_RETRY_DELAYS_MS) {
    await sleep(delayMs);
    if ((await ensureCurrentUser()) !== null) return true;
  }
  return false;
}

async function postNotificationRegistrationWithRetry(
  registrationId: string,
  device: "ios" | "android" | "web",
  permission: NotificationPermissionStatus | null
): Promise<boolean> {
  for (let attempt = 0; ; attempt++) {
    try {
      await apiClient.post("/webapp/private/notification/registrations", {
        json: { registrationId, device, permission },
      });
      return true;
    } catch (error) {
      const isMissingBackendUser =
        error instanceof ClientError &&
        (error.responseStatus === 404 || error.responseStatus === 401);
      if (
        !isMissingBackendUser ||
        attempt >= PUSH_REGISTRATION_RETRY_DELAYS_MS.length
      ) {
        return false;
      }
      await sleep(PUSH_REGISTRATION_RETRY_DELAYS_MS[attempt]);
    }
  }
}

async function trySyncRegistrationToken(
  registrationId: string,
  device: "ios" | "android" | "web"
): Promise<boolean> {
  const isUserReady = await waitForBackendUserReady();
  if (!isUserReady) return false;
  // Read the permission at sync time, not at prompt time: it changes in system
  // settings without the token changing, and this sync runs on every foreground —
  // which is how a revoked device ever gets noticed.
  const permission = await readOsNotificationPermission();
  return postNotificationRegistrationWithRetry(
    registrationId,
    device,
    permission
  );
}

function isSilentFirebaseAuthError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message;
  const code = error instanceof FirebaseError ? error.code : "";

  return SILENT_FIREBASE_ERRORS.some(
    (needle) => msg.includes(needle) || code === needle
  );
}

// Persist pending credential for provider collision linking across page refreshes.
// Shape is owned by Firebase — we round-trip the JSON payload it hands us.
const pendingLinkCredentialStore = sessionStore<unknown>(
  "pendingLinkCredential",
  null,
  z.unknown()
);

function getPendingCredential(): OAuthCredential | null {
  const stored = pendingLinkCredentialStore.get();
  if (stored === null || stored === undefined) return null;
  if (typeof stored !== "object") return null;
  return OAuthCredential.fromJSON(stored);
}

function setPendingCredential(cred: OAuthCredential | null): void {
  pendingLinkCredentialStore.set(cred === null ? null : cred.toJSON());
}

async function runOAuthFlow(opts: {
  providerLabel: "Google" | "Apple";
  /**
   * Where the user launched this flow from, and what they thought they were
   * doing. Both are carried into `auth_failed` so it joins cleanly with
   * `auth_method_selected`, which the caller already fires with the same pair.
   * The declared intent is the caller's, not the outcome: on a failure we never
   * reach the probe that would tell us whether the account already existed.
   */
  surface: SignupSurface;
  intent: AuthIntent;
  /** See `loginAndRedirect`. */
  returnTo: string | null;
  signIn: () => Promise<UserCredential>;
}): Promise<void> {
  const accountType = opts.providerLabel === "Google" ? "google" : "apple";
  signupInFlightStore.set(true);
  try {
    const credentials = await opts.signIn();

    // Link pending credential from a previous provider collision
    const pendingCredential = getPendingCredential();
    if (pendingCredential !== null) {
      await linkWithCredential(credentials.user, pendingCredential).catch(
        (error) => {
          console.error("Failed to link credential:", error);
        }
      );
      setPendingCredential(null);
    }

    let userExists: boolean;
    try {
      const token = await getFirebaseToken();
      if (token === null) {
        displayToast(t("Something went wrong. Please try again."), "error");
        await firebaseLogout();
        return;
      }
      // No `token` in the query: it is a live Firebase ID token and a url is
      // written down by everything it passes, Cloud Run's access logs included
      // (SEC-02). The api client attaches this same token as a bearer header,
      // which those logs do not record. The fetch above stays as the guard that
      // there IS a token to attach.
      const probe = await apiClient.get("/webapp/public/users/probe", {});
      userExists = probe.body.exists;
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      await firebaseLogout();
      return;
    }

    if (userExists) {
      await loginAndRedirect(accountType, opts.returnTo);
      return;
    }

    await signupFromUserCredential(credentials);
    try {
      await fetchAndCacheCurrentUser();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      await firebaseLogout();
      return;
    }
    gaEventAuthSignup(credentials.user.uid, accountType);
    replace("/signup/onboarding");

    // Sync push token AFTER user is guaranteed to exist in DB
  } catch (error: unknown) {
    // Provider collision: store credential, show which provider to use
    if (
      error instanceof FirebaseError &&
      error.code === "auth/account-exists-with-different-credential"
    ) {
      const cred = OAuthProvider.credentialFromError(error);
      if (cred !== null) setPendingCredential(cred);

      const email =
        typeof error.customData?.email === "string"
          ? error.customData.email
          : undefined;

      if (email === undefined) {
        gaEventAuthFailed({
          code: "provider_collision",
          method: accountType,
          intent: opts.intent,
          surface: opts.surface,
          existing_provider: null,
        });
        displayToast(
          t(
            "This email is already registered with {{provider}}. Please sign in with {{provider}} to continue.",
            {
              provider: "another provider",
            }
          ),
          "error"
        );
      } else {
        const methods = await fetchSignInMethodsForEmail(auth, email);
        gaEventAuthFailed({
          code: "provider_collision",
          method: accountType,
          intent: opts.intent,
          surface: opts.surface,
          existing_provider: getExistingProvider(methods),
        });
        const provider = getProviderLabel(methods);
        displayToast(
          t(
            "This email is already registered with {{provider}}. Please sign in with {{provider}} to continue.",
            { provider }
          ),
          "error"
        );
      }
      return;
    }

    // Clean up orphaned Firebase session to prevent broken auth state
    await firebaseLogout();

    if (!(error instanceof Error)) return;
    // Silent errors are user-initiated aborts (dismissed popup, cancelled
    // native sheet), not failures — tracking them would drown the real ones.
    if (isSilentFirebaseAuthError(error)) {
      console.warn(
        `Firebase: silent auth error (${opts.providerLabel}).`,
        error
      );
      return;
    }
    console.error(`Firebase: couldn't sign in (${opts.providerLabel}).`, error);

    gaEventAuthFailed({
      code:
        error instanceof FirebaseError
          ? resolveAuthErrorCode(error.code)
          : "unknown",
      method: accountType,
      intent: opts.intent,
      surface: opts.surface,
      existing_provider: null,
    });

    if (isStaging) {
      displayToast(
        `Firebase: couldn't sign in (${opts.providerLabel}): ${error.message}`,
        "error"
      );
    } else {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  } finally {
    signupInFlightStore.set(false);
  }
}

function getProviderLabel(methods: string[]): string {
  if (methods.includes("google.com")) return "Google";
  if (methods.includes("apple.com")) return "Apple";
  if (methods.includes("password")) return "email/password";
  return methods.at(0) ?? "another provider";
}

/**
 * Analytics counterpart of `getProviderLabel`: the same lookup, but returning a
 * literal instead of a display string. Kept separate on purpose — the label is a
 * UI string that translators may touch, this one is a report dimension.
 */
function getExistingProvider(
  methods: string[]
): "apple" | "google" | "email" | null {
  if (methods.includes("google.com")) return "google";
  if (methods.includes("apple.com")) return "apple";
  if (methods.includes("password")) return "email";
  return null;
}

/**
 * Which provider owns an address, for analytics only — never for UI decisions.
 * Returns null when Firebase won't say: with email-enumeration protection on
 * (the default on recent projects) this always comes back empty, which is
 * exactly the signal that we cannot attribute the failure to a provider.
 */
export async function lookupExistingProvider(
  email: string
): Promise<"apple" | "google" | "email" | null> {
  const methods = await fetchSignInMethodsForEmail(auth, email).catch(() => []);
  return getExistingProvider(methods);
}

export async function loginWithGoogle(context: {
  surface: SignupSurface;
  intent: AuthIntent;
  returnTo: string | null;
}): Promise<void> {
  return runOAuthFlow({
    providerLabel: "Google",
    surface: context.surface,
    intent: context.intent,
    returnTo: context.returnTo,
    signIn: async () => {
      if (isIOSApp()) return iosAuthSignIn("google");
      if (isAndroidApp()) return androidAuthSignIn();
      return signInWithPopup(
        auth,
        googleProvider,
        browserPopupRedirectResolver
      );
    },
  });
}

export async function loginWithApple(context: {
  surface: SignupSurface;
  intent: AuthIntent;
  returnTo: string | null;
}): Promise<void> {
  return runOAuthFlow({
    providerLabel: "Apple",
    surface: context.surface,
    intent: context.intent,
    returnTo: context.returnTo,
    signIn: async () => {
      if (isIOSApp()) return iosAuthSignIn("apple");
      return signInWithPopup(auth, appleProvider, browserPopupRedirectResolver);
    },
  });
}

/** Native Android Auth */
const androidAuthSignIn = async (): Promise<UserCredential> => {
  const callback = await androidSignIn();

  if (callback?.success === true) {
    const { idToken } = callback;
    const credentials: OAuthCredential = GoogleAuthProvider.credential(idToken);
    return await signInWithCredential(auth, credentials);
  }

  const errorMessage =
    callback !== undefined && callback !== null && !callback.success
      ? callback.error
      : "Unknown error";
  throw new Error(`Failed to sign in with Android: ${errorMessage}`);
};

async function iosAuthSignIn(
  provider: "apple" | "google"
): Promise<UserCredential> {
  const callback =
    provider === "apple" ? await iosAppleSignIn() : await iosGoogleSignIn();

  if (callback.success) {
    const credentials: OAuthCredential =
      provider === "apple"
        ? appleProvider.credential({
            idToken: callback.idToken,
            rawNonce: callback.rawNonce,
          })
        : GoogleAuthProvider.credential(callback.idToken);

    return signInWithCredential(auth, credentials);
  }

  displayToast(t("Something went wrong. Please try again."), "error");
  throw new Error(callback.error);
}

/**
 * The client this account is created on (`users.signup_platform`). The native
 * shells are told apart by their bridge, a phone browser from a desktop one
 * by the user agent (`currentClient`). iPad Safari announces a Mac, so an iPad
 * browser signup is `desktop_web`.
 */
function signupPlatform(): Api["post"]["/webapp/public/users"]["input"]["json"]["signupPlatform"] {
  const client = currentClient();
  switch (client) {
    case "ios_app":
    case "android_app":
    case "desktop_web":
      return client;
    case "ios_web":
    case "android_web":
      return "mobile_web";
    default:
      throw new Error(client satisfies never);
  }
}

async function signupFromUserCredential(credentials: UserCredential) {
  const email = credentials.user.email;
  if (!email) throw new Error("Email not found");
  const userInfo = credentials.user.providerData[0];
  if (!userInfo) throw new Error("User information not found");

  let firstname = "";
  let lastname = "";
  // TODO: Use OAuth standard fields if available (given_name and family_name)
  if (userInfo.displayName) {
    // Fallback to splitting name: first word is firstname, rest is lastname
    const nameParts = userInfo.displayName.trim().split(/\s+/);
    firstname = nameParts[0] || "";
    lastname = nameParts.slice(1).join(" ") || "";
  }

  // Apple re-auth: displayName is empty after first sign-in
  if (firstname === "") firstname = email.split("@")[0] || "User";
  if (lastname === "") lastname = "-";

  // No React context here, so read the persisted invite store directly (same
  // QR / short-link code the email-password path forwards). TTL-guarded so an
  // expired invite doesn't attribute an OAuth signup to a stale room.
  const pendingInvite = hasPendingRoomInvite()
    ? roomInviteCodeStore.get()
    : null;

  return apiClient
    .post("/webapp/public/users", {
      json: {
        uid: credentials.user.uid,
        email,
        firstname,
        lastname,
        country: null,
        device: getCurrentOS(),
        language:
          parseSupportedLanguage(i18n.resolvedLanguage ?? "") ??
          DEFAULT_LANGUAGE,
        invitationCode:
          pendingInvite === null ? null : pendingInvite.roomInviteCode,
        // The same channel the invite join sends for this invite, with the same
        // "public app" for an invite that was not a QR scan.
        invitationChannel:
          pendingInvite === null
            ? null
            : pendingInvite.roomDiscoveryChannel === null
              ? "public app"
              : pendingInvite.roomDiscoveryChannel,
        referral: signupReferral(),
        signupPlatform: signupPlatform(),
        firstTouch: signupFirstTouch(),
      },
    })
    .then((r) => {
      signupSessionUidStore.set(credentials.user.uid);
      // The record has done its only job: a later account on this device,
      // after any sign-out, must not inherit it.
      clearFirstTouch();
      return r.body;
    });
}

async function fetchAndCacheCurrentUser() {
  return apiClient
    .get("/webapp/private/users/me", {
      query: {
        language: normalizeLanguage(
          i18n.resolvedLanguage ?? navigator.language
        ),
      },
    })
    .then((response) => {
      replaceCurrentUserData(response.body);
      return response.body;
    });
}

/**
 * Email-OTP login: the code was already accepted by the API, which handed back a
 * Firebase custom token. This is the ONLY Firebase step left on the email path
 * (PRD §11) — everything before it is ours.
 */
export async function loginWithEmailOtp(
  customToken: string,
  returnTo: string | null
): Promise<AuthResult> {
  try {
    await signInWithCustomToken(auth, customToken);
    await loginAndRedirect("email", returnTo);
    return { isSuccess: true };
  } catch (error: unknown) {
    console.warn("Couldn't sign in with the email code.", error);

    // A half-open Firebase session is worse than none.
    await firebaseLogout();

    return {
      isSuccess: false,
      errorCode:
        error instanceof FirebaseError ? error.code : "auth/internal-error",
    };
  }
}

/**
 * Email-OTP signup, first half: create the account row for the uid the API just
 * minted from the verified code.
 *
 * Split from the sign-in below on purpose, and the caller must run them in this
 * order. Every private route resolves the caller by Firebase uid, so a browser
 * holding a session for a uid with no row gets a 401 on `/users/me` and on
 * every other private route. Signing in first would leave the user, mid-signup,
 * with a session that every request refuses. The gap between the two is also where the
 * signup flow records what happened, because the sign-in unmounts it.
 *
 * Returns false having shown its own toast; the caller stays on the slide.
 */
export async function createAccountForVerifiedEmail(
  uid: string,
  profileData: {
    email: string;
    firstname: string;
    lastname: string;
    country: string | null;
    language: SupportedLanguage;
    invitationCode: string | null;
    invitationChannel: Api["post"]["/webapp/public/users"]["input"]["json"]["invitationChannel"];
  },
  surface: SignupSurface
): Promise<boolean> {
  // No `signupInFlightStore` here, deliberately. That flag exists to stop
  // `/users/me` firing between the Firebase auth flip and the row being
  // created — an ordering this path does not have, because the row lands first.
  try {
    await apiClient.post("/webapp/public/users", {
      // The route reads the trusted-device cookie as an address proof, for a
      // signup that skipped the code entirely. The shared client omits
      // credentials by default (`shared/request.ts`), and without this the
      // cookie never arrives: the account starts unvalidated and receives a
      // confirmation email asking it to prove an address it already proved.
      credentials: "include",
      json: {
        uid,
        email: profileData.email,
        firstname: profileData.firstname,
        lastname: profileData.lastname,
        // The country slide comes AFTER the code slide, so it is unknown here
        // and the finalize PATCH fills it in. Same shape as an SSO signup,
        // which has never had a country at creation either.
        country: profileData.country,
        device: getCurrentOS(),
        language: profileData.language,
        invitationCode: profileData.invitationCode,
        invitationChannel: profileData.invitationChannel,
        referral: signupReferral(),
        signupPlatform: signupPlatform(),
        firstTouch: signupFirstTouch(),
      },
    });
    signupSessionUidStore.set(uid);
    clearFirstTouch();
    return true;
  } catch (error) {
    // The address took an account between the code request and now (a parallel
    // SSO signup). Nothing to compensate: the Firebase account belongs to that
    // address either way, and no session was opened.
    if (error instanceof ClientError && error.responseStatus === 409) {
      gaEventAuthFailed({
        code: "email_already_in_use",
        method: "email",
        intent: "signup",
        surface,
        existing_provider: await lookupExistingProvider(profileData.email),
      });
      displayToast(
        t(
          "📬 An account associated with this email address already exists. Login with this email address or create a new account."
        ),
        "error"
      );
      replace("/login");
      return false;
    }

    gaEventAuthFailed({
      code: "unknown",
      method: "email",
      intent: "signup",
      surface,
      existing_provider: null,
    });
    displayToast(t("Something went wrong. Please try again."), "error");
    return false;
  }
}

/**
 * Email-OTP signup, second half: open the session for the account that now
 * exists. No redirect on success — the caller is mid-carousel and stays there.
 *
 * A failure here is the awkward case: the account exists and the code is spent,
 * so there is nothing to retry on the slide. The way back in is a fresh code,
 * which is what /login asks for.
 */
export async function signInWithMintedToken(
  customToken: string,
  uid: string
): Promise<void> {
  try {
    await signInWithCustomToken(auth, customToken);
  } catch (error) {
    console.warn("Couldn't open the session for the verified code.", error);
    displayToast(t("Something went wrong. Please try again."), "error");
    otpCredentialStore.set(null);
    resetOnboardingFlow();
    replace("/login");
    return;
  }
  await revalidateApiCache();
  reloadCurrentUser();
  gaEventAuthSignup(uid, "email");
  void syncPushTokenIfPossible({ force: true, reason: "auth" }).catch(
    console.error
  );
}

/**
 * Open the app for a user who just signed in to an existing account.
 *
 * `returnTo` is the page the login was started from, when the door that started
 * it sat over one (the desktop gate). It wins over a memorized path, and either
 * one wins over the pinned room: a reader who followed a link to a page came for
 * that page.
 */
async function loginAndRedirect(
  accountType: "email" | "google" | "apple",
  returnTo: string | null
) {
  let user: Awaited<
    ReturnType<typeof apiClient.get<"/webapp/private/users/me">>
  >["body"];
  try {
    user = await fetchAndCacheCurrentUser();
  } catch {
    displayToast(t("Something went wrong. Please try again."), "error");
    await firebaseLogout();
    return;
  }
  gaEventAuthLogin(user.uid, accountType);
  await revalidateApiCache();

  void syncPushTokenIfPossible({ force: true, reason: "auth" }).catch(
    console.error
  );

  const authRedirectPath =
    returnTo === null ? getPostLoginRedirectPath() : returnTo;
  const isOnboarded = isUserOnboarded(user);

  // Records that the account has logged in once, wherever this login lands.
  if (user.meta.firstLogin !== true) {
    try {
      await apiClient.post("/webapp/private/users/me/first-login");
    } catch (error) {
      // Non-fatal: first-login marker is best-effort, don't block login flow.
      console.warn("first-login call failed", error);
    }
  }

  // Redirect to onboarding when profile is incomplete (no professionModel).
  // Covers: OAuth-provisioned admin accounts (have name from Google, no profession)
  // and legacy users whose profile was never fully filled out.
  //
  // The destination chosen above survives this detour: `finishOnboarding`
  // opens it once the profile is complete. It is written back to the store so
  // that a `returnTo` wins over an older memorized path there too.
  if (!isOnboarded) {
    if (authRedirectPath === "") clearPostLoginRedirectPath();
    else memorizePostLoginRedirectTo(authRedirectPath);
    void replace("/signup/onboarding");
    return;
  }

  // Notifications are now handled inside OnboardingPage — no longer block login

  if (authRedirectPath) {
    clearPostLoginRedirectPath();
    void replace(await resolvePostAuthDestination(authRedirectPath, user));
    return;
  }

  if (hasPendingRoomInvite()) {
    void replace("/signup/onboarding");
    return;
  }

  // The pinned room, else the B2C home — `getUserStartingPage` owns that answer
  // (JuisciGPT today) so this exit cannot drift from the root `/` redirect.
  void replace(await getUserStartingPage(user));
}

/** Firebase Auth Handler */

/**
 * Sign out, and take the reader's data with it.
 *
 * React Query persists successful responses to `localStorage`
 * (`REACT_QUERY_OFFLINE_CACHE`, see `services/queryPersistence`). Those
 * responses are the account's: their feed, their saved papers, their rooms.
 * Signing out without discarding them leaves that data on the device for
 * whoever opens the browser next, and — because a persisted cache is served
 * before any request resolves — lets it render on a page the visitor is no
 * longer entitled to. On a shared machine the next reader is a different
 * person.
 *
 * Both halves are needed. `removeClient` drops what is on disk; `clearApiCache`
 * drops what is already in memory — which `removeClient` does not touch and
 * which would otherwise be re-persisted on the next write — and cancels the
 * requests still in flight, so none of them can land and refill it.
 *
 * This runs on EVERY logout, including the involuntary ones: the Firebase
 * observer calls it when a token refresh proves the credential invalid
 * (`useFirebaseUser`), and the auth error handler calls it on the forced-OAuth
 * 403 (`createAuthErrorHandler`'s `logout`), so those clear the device too.
 */
export async function firebaseLogout() {
  // Clear the email proof before the first await. The auth error handler starts
  // this logout without waiting, and the next route may mount in the same tab.
  otpCredentialStore.set(null);
  // The visual abstract run lists are the account's too. They are emptied
  // before anything can yield, for the reason the first disk wipe below gives.
  clearVisualAbstractRunsOnDevice();
  // So is a mailed code: the next `/welcome` boot must not reopen its screen.
  clearPendingEmailCode();
  // And a desktop code request: the next person on the tab must not meet this
  // address, with a code mailed to it, over a private page.
  desktopCodeRequestStore.set(null);

  // The disk wipe runs TWICE, and each call answers a different race.
  //
  // FIRST, before this function can yield. The involuntary logout does not
  // await it: `createAuthErrorHandler.performRedirect` fires `logout()` and
  // calls `location.replace` on the next line, so the document can start
  // unloading while this is suspended, and anything behind an `await` never
  // runs. `createSyncStoragePersister.removeClient` writes to `localStorage`
  // inline, so this call lands in the tick that started it.
  await localStoragePersister.removeClient();

  // Firebase goes out BEFORE the in-memory cache is emptied, and that order is
  // load bearing (`useLogOutUser` says so too): emptying it first leaves every
  // mounted observer refetching with a token that still works, which storms the
  // API and re-persists what was just dropped.
  await auth.signOut();
  resetOnboardingFlow();
  // Again after the await: a run settling while `signOut()` was in flight can
  // have written to them since.
  clearVisualAbstractRunsOnDevice();
  clearApiCache();

  // LAST, because the window between the two wipes belongs to whatever resolved
  // while `signOut()` was in flight. The persister is throttled and writes the
  // WHOLE cache, so a single late response puts the account back on disk.
  await localStoragePersister.removeClient();
  // And the cache no longer belongs to anyone signed in: a write still carrying
  // the account's snapshot is refused from here on (`createOwnedPersistence`).
  await adoptCacheOwner(null);
}

export function firebaseRefresh() {
  auth.currentUser
    ?.getIdToken()
    .then(function (idToken: string) {
      console.log("fbRefresh", idToken);
    })
    .catch(function (error: unknown) {
      console.log("fbRefresh", error);
    });
}

// --- iOS auth recovery ---
// When iOS reloads the webview during a native sign-in flow (memory warning,
// WKWebView process termination), the JS callback promise is lost. The fixed
// callback name in ios.ts survives the reload and dispatches here so we can
// complete the sign-in that the user already finished on the native side.

setIOSAuthRecoveryHandler((data: IOSAuthRecoveryData) => {
  void recoverIOSAuth(data);
});

// --- Native FCM token refresh ---
// When Firebase SDK refreshes the FCM token (after days of inactivity, reinstall, etc.),
// the native app pushes the new token to JS via window.__onNativeFCMTokenRefresh.
// We sync it with the backend so push notifications keep working.
// Register both refresh handlers UNCONDITIONALLY. These setters only store the
// callback; the window binding that actually invokes it is installed OS-gated
// (reliably, via user-agent) inside tools/ios.ts + tools/android.ts, so the
// wrong-platform handler simply never fires. Gating these on isIOSApp() /
// isAndroidApp() was a bug: those probe the native WKWebView bridge, which may
// not be ready at module-eval on a slow cold start — so the handler was never
// registered for the whole session and a post-reinstall token refresh (the main
// way a fresh FCM token reaches us once APNs re-binds) was silently dropped.
setIOSTokenRefreshHandler((token) => {
  void registerPushToken(token, "ios", {
    force: true,
    reason: "tokenRefresh",
  }).catch(console.error);
});
setAndroidTokenRefreshHandler((token) => {
  void registerPushToken(token, "android", {
    force: true,
    reason: "tokenRefresh",
  }).catch(console.error);
});

// --- Native foreground push ---
// When an FCM push arrives while the app is open, the native layers suppress
// the system banner and forward the payload via window.__onNativePushReceived
// so we can show an in-app toast instead.
if (isIOSApp()) setIOSForegroundPushHandler(handleForegroundPush);
if (isAndroidApp()) setAndroidForegroundPushHandler(handleForegroundPush);

async function recoverIOSAuth(data: IOSAuthRecoveryData): Promise<void> {
  if (!data.result.success) return; // User cancelled — nothing to recover

  signupInFlightStore.set(true);
  try {
    const credential: OAuthCredential =
      data.provider === "apple"
        ? appleProvider.credential({
            idToken: data.result.idToken,
            rawNonce: data.result.rawNonce,
          })
        : GoogleAuthProvider.credential(data.result.idToken);

    const userCredential = await signInWithCredential(auth, credential);

    let userExists: boolean;
    try {
      const token = await getFirebaseToken();
      if (token === null) {
        displayToast(t("Something went wrong. Please try again."), "error");
        await firebaseLogout();
        return;
      }
      // No `token` in the query: it is a live Firebase ID token and a url is
      // written down by everything it passes, Cloud Run's access logs included
      // (SEC-02). The api client attaches this same token as a bearer header,
      // which those logs do not record. The fetch above stays as the guard that
      // there IS a token to attach.
      const probe = await apiClient.get("/webapp/public/users/probe", {});
      userExists = probe.body.exists;
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      await firebaseLogout();
      return;
    }

    if (userExists) {
      await loginAndRedirect(data.provider, null);
      return;
    }

    await signupFromUserCredential(userCredential);
    try {
      await fetchAndCacheCurrentUser();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      await firebaseLogout();
      return;
    }
    gaEventAuthSignup(userCredential.user.uid, data.provider);
    replace("/signup/onboarding");
  } catch (error) {
    console.error("iOS auth recovery failed:", error);
    await firebaseLogout();
  } finally {
    signupInFlightStore.set(false);
  }
}
